package ai.jessie.web.settings

import ai.jessie.web.theme.ButtonSize
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.CardBodyStyle
import ai.jessie.web.theme.CardFootStyle
import ai.jessie.web.theme.CardStackStyle
import ai.jessie.web.theme.CardStyle
import ai.jessie.web.theme.FieldsetStyle
import ai.jessie.web.theme.Grid2Style
import ai.jessie.web.theme.Grid3Style
import ai.jessie.web.theme.LegendStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.VisuallyHiddenStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Fieldset
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.Legend
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLFieldSetElement

// Steps 3–5: one form, one preview request. The artboard's per-card "Speichern" would be a
// behaviour change, so the four cards are fieldsets of this form and the last card holds the
// single footer.
@Composable
internal fun settingsForm(state: SettingsUiState, actions: SettingsActions) {
    val draft = state.draft ?: return
    Form(
        attrs = {
            addEventListener("submit") {
                it.preventDefault()
                actions.preview()
            }
        }
    ) {
        Fieldset(
            attrs =
                FieldsetStyle.toAttrs {
                    if (state.busy) attr("disabled", "")
                }
        ) {
            Legend(attrs = VisuallyHiddenStyle.toAttrs()) { Text("Einstellungen bearbeiten") }
            Div(attrs = CardStackStyle.toAttrs()) {
                settingsSections.forEach { section -> sectionCard(section, state, actions) }
                vatCard(state, actions)
                taxonomyCard("documentCategories", state, draft.categories) { update ->
                    actions.change { it.copy(categories = update(it.categories)) }
                }
                taxonomyCard(
                    "documentFolders",
                    state,
                    draft.folders,
                    footer = { formFooter(state, actions) },
                ) { update ->
                    actions.change { it.copy(folders = update(it.folders)) }
                }
            }
        }
    }
}

@Composable
private fun formFooter(state: SettingsUiState, actions: SettingsActions) {
    themedButton("Abbrechen", actions.cancel, disabled = state.busy)
    themedButton(
        "Änderungen prüfen",
        {},
        variant = ButtonVariant.DARK,
        disabled = state.busy,
        submit = true,
    )
}

// A fieldset shown as a card (`CustomerForm.kt`, groupCard): the legend is the card head, the
// optional [footer] the card's footer bar.
@Composable
internal fun groupCard(
    legend: String,
    description: String? = null,
    footer: (@Composable () -> Unit)? = null,
    attrs: (AttrsScope<HTMLFieldSetElement>.() -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Div(attrs = CardStyle.toAttrs()) {
        Fieldset(attrs = FieldsetStyle.toAttrs { attrs?.invoke(this) }) {
            Legend(attrs = LegendStyle.toAttrs()) { Text(legend) }
            Div(attrs = CardBodyStyle.toAttrs()) {
                description?.let { hint(it) }
                content()
            }
        }
        if (footer != null) Div(attrs = CardFootStyle.toAttrs()) { footer() }
    }
}

@Composable
internal fun hint(text: String) {
    Div(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text(text) }
}

@Composable
internal fun smallAction(label: String, busy: Boolean, action: () -> Unit) {
    Div(attrs = RowStyle.toAttrs()) {
        themedButton(label, action, size = ButtonSize.SMALL, disabled = busy)
    }
}

@Composable
private fun sectionCard(
    section: SettingsSection,
    state: SettingsUiState,
    actions: SettingsActions,
) {
    groupCard(section.title, section.description) {
        section.rows.forEach { row ->
            when (row.size) {
                1 -> scalarControl(row.single(), state, actions)
                2 ->
                    Div(attrs = Grid2Style.toAttrs()) {
                        row.forEach { scalarControl(it, state, actions) }
                    }
                else ->
                    Div(attrs = Grid3Style.toAttrs()) {
                        row.forEach { scalarControl(it, state, actions) }
                    }
            }
        }
        sectionHints(section.title, state)
    }
}

@Composable
private fun scalarControl(name: String, state: SettingsUiState, actions: SettingsActions) {
    val draft = state.draft ?: return
    val field =
        SettingsField(
            name,
            settingLabels.getValue(name),
            draft.fields.getValue(name),
            state.problems[name],
        )
    val change: (String) -> Unit = { value ->
        actions.change { it.copy(fields = it.fields + (name to value)) }
    }
    when (name) {
        "defaultCurrency" ->
            settingsSelect(field, listOf("CHF", "EUR", "USD").associateWith { it }, change)
        "automaticDocumentExtraction" ->
            settingsSelect(field, mapOf("true" to "Aktiviert", "false" to "Deaktiviert"), change)
        else -> settingsField(field, change)
    }
}

// The existing guidance sentences, placed with the card they concern.
@Composable
private fun sectionHints(title: String, state: SettingsUiState) {
    val draft = state.draft ?: return
    when (title) {
        "Adresse" ->
            hint(
                "Die Zeitzone kann erst geändert werden, wenn alle laufenden oder noch " +
                    "abzuschliessenden Timer geschlossen sind."
            )
        "Rechnungsinformationen" -> {
            val floor =
                state.current
                    ?.invoiceSequences
                    ?.firstOrNull { it.prefix == draft.fields["invoicePrefix"]?.trim() }
                    ?.nextNumber ?: 1
            hint(
                "Mindestwert für die nächste Nummer dieses Präfixes: $floor. " +
                    "Frühere Präfixe behalten ihren Nummernstand."
            )
            hint(
                "Automatische Extraktion erfolgt nur nach der globalen Freigabe. " +
                    "Die Buchhaltungsrelevanz bleibt je Dokument unabhängig."
            )
        }
    }
}

// Handlers update the current draft, not the one captured at composition, so several inputs
// arriving before a recomposition (autofill, paste) do not overwrite each other.
internal fun <T> List<T>.updated(index: Int, update: (T) -> T): List<T> = mapIndexed { i, old ->
    if (i == index) update(old) else old
}
